import { IAddress, Borough } from './address';
import { INamedEntity, Gender } from './general';
import { IFilePreview } from './file';
import { BadgeStatus, BadgeType } from './badge';
import { BadgeModel, UserModel } from '.';
import { InvitationStatus } from 'modules/models/user';

export enum WorkerStatusFilter {
  NOT_SET = 0,
  ACTIVE = 1,
  PENDING_REGISTRATION = 2,
  MIGRATED = 4,
  PENDING_PROJECT = 5,
}

export enum WorkerStatus {
  PENDING_REGISTRATION = 0,
  ACTIVE = 1,
  EXPIRED = 2,
  NOT_INVITED = 3,
  MIGRATED = 4,
}

export enum WorkerProjectInvitationStatus {
  PENDING_REGISTRATION = 0,
  ACTIVE = 1,
  DECLINED = 2,
}

export enum InviteMethod {
  EMAIL = 1,
  MOBILE_PHONE = 2,
  BOTH = 3,
}

export const InviteMethodMap = {
  [InviteMethod.EMAIL]: 'Email',
  [InviteMethod.MOBILE_PHONE]: 'Mobile Phone',
  [InviteMethod.BOTH]: 'Mobile Phone And Email',
};

export const InviteMethodValues = [
  { value: InviteMethod.MOBILE_PHONE, label: 'Mobile phone' },
  { value: InviteMethod.EMAIL, label: 'Email' },
  { value: InviteMethod.BOTH, label: 'Mobile Phone And Email' },
];

export enum WorkerDetailsTabs {
  PROJECTS = 'projects',
  ACTIVITY = 'activity',
  CERTIFICATIONS = 'certifications',
  TRAINIGS = 'trainings',
  FCA_LEARNING = 'fcaLearning',
  OBSERVATIONS = 'observations',
  INFORMATION = 'information',
}

export enum WorkerActivityType {
  UNKNOWN = 0,
  ENTRANCE = 1,
  EXIT = 2,
  DENY_ENTRANCE = 3,
  DENY_EXIT = 4,
}

export enum GeographicLocationType {
  COUNTRY = 1,
  STATE = 2,
}

export const workerActivityNameMap = {
  [WorkerActivityType.UNKNOWN]: { name: 'unknown', color: '#000000' },
  [WorkerActivityType.ENTRANCE]: { name: 'Check in', color: '#37A66C' },
  [WorkerActivityType.EXIT]: { name: 'Check out', color: '#E03534' },
  [WorkerActivityType.DENY_ENTRANCE]: { name: 'Deny Check in', color: '#000000' },
  [WorkerActivityType.DENY_EXIT]: { name: 'Deny Check out', color: '#000000' },
};
export interface IWorkerActivity {
  id: string;
  type: WorkerActivityType;
  dateTime: string;
  location?: string;
  project: INamedEntity;
  source?: string;
  locationCode?: string;
}

export interface IWorkerObservation {
  id: string;
  type: INamedEntity;
  date: string;
  project: INamedEntity;
  company?: INamedEntity;
  standardDescription: INamedEntity;
  comments?: string;
  files?: IFilePreview[];
  owner?: INamedEntity;
  projectVisibility?: ProjectVisibilityType;
}

export interface IEthnicity extends INamedEntity {}

export interface ILanguage extends INamedEntity {}

export interface IJobTitle extends INamedEntity {
  masterList?: boolean;
}

export interface ISocJobTitle extends INamedEntity {}

export interface ITradeStatus extends INamedEntity {}

export interface ILanguageTurnerProtocol extends INamedEntity {}

export interface ISkilledTrade extends INamedEntity {
  masterList?: boolean;
}

export interface IProjectList extends INamedEntity {}

export interface IIdentificationType extends INamedEntity {
  geographicLocationsIds: string[];
}

export interface IIdentificationGeographicLocation extends INamedEntity {}

export interface IGeographicLocation extends INamedEntity {
  type: GeographicLocationType.COUNTRY | GeographicLocationType.STATE;
  country?: string;
}

export interface ISOCJobTitle extends INamedEntity {}

export interface IWorkerCompany extends INamedEntity {
  hasUniversalBadge?: boolean;
}

export enum PaymentType {
  HOURLY = 1,
  SALARY = 2,
}

export interface IWorker {
  id: string;
  userId?: string;
  company?: IWorkerCompany;
  companies?: Array<IWorkerCompany>;
  companyLabel?: number;
  firstName: string;
  middleName: string;
  lastName: string;
  mobilePhoneNumber: string;
  email: string;
  dateOfBirth: string;
  gender: Gender;
  ethnicity?: IEthnicity;
  ethnicityId?: string;
  socialSecurityNumber: string;
  isVeteran: boolean;
  allergies: string;
  identificationType?: IIdentificationType;
  identificationTypeId?: string;
  identificationNumber: string;
  identificationGeographicLocation: IIdentificationGeographicLocation;
  identificationGeographicLocationId: string;
  primaryLanguage?: ILanguage;
  primaryLanguageId?: string;
  otherPrimaryLanguage?: string;
  phoneNumber: string;
  isSkilled: boolean;
  otherTrade: string;
  address: IAddress;
  emergencyContactName: string;
  emergencyContactPhone: string;
  emergencyContactRelationship: string;
  isSupervisor: boolean;
  laborUnionNumber: string;
  trades?: ISkilledTrade[];
  tradesIds?: string[];
  invitationStatus: InvitationStatus;
  isAffiliatedToLaborUnion: boolean;
  projectsCount?: number;
  pictureUrl?: string;
  createdAt?: string;
  certificationsCount?: number;
  trainingsCount?: number;
  observationsCount?: number;
  electronicBadgeCode?: string;
  inviteMethod: InviteMethod;
  line1: string;
  line2: string;
  city: string;
  stateCode: string;
  zipCode: string;
  country?: INamedEntity;
  countryId?: string;
  county?: string;
  attention1?: string;
  attention2?: string;
  stateName?: string;
  borough?: Borough;
  latitude: number;
  longitude: number;
  otherTradesList?: ISkilledTrade[];
  otherIdentificationType?: string;
  badgeFieldValues?: any;
  projectBadgeTemplates?: any;
  badgeTemplateId?: any;
  exemptedCertificationGroups?: any;
  exemptedCertifications?: any;
  exemptedTrainingGroupIds?: any;
  exemptedTrainingIds?: any;
  exemptedLearningGroups?: any;
  exemptedCourses?: any;
  projectsAcceptedCount?: number;
  governmentDocumentFiles?: {
    displayName: string;
    fileName: string;
    fileSize: number;
    id: string;
    url: string;
  }[];
  FromOperationsWeb?: boolean;
  resendInvitation?: boolean;
  forceCreate?: boolean;
  canEditEmail?: boolean;
  canEditMobilePhoneNumber?: boolean;
  activeBadges?: number;
  invitationSentOn?: string;
  assignedProjects?: any;
  projectsAssignedCount?: number;
  companyWorkerId?: string;
  workerTokenInformation?: any;
}

export interface IExistingWorker {
  fullName: string;
  companyName?: string;
  mobilePhoneNumber?: string;
  email: string;
  dateOfBirth: string;
  stateCode?: string;
  stateName?: string;
  pictureUrl: string;
  socialSecurityNumber?: string;
}

export interface IWorkerProject extends IWorker {
  workerProjectStatus: WorkerProjectInvitationStatus;
  badgeStatus: BadgeModel.BadgeStatus;
}

export enum DetailTabType {
  PROJECTS = 'projects',
  ACTIVITY = 'activity',
  CERTIFICATIONS = 'certifications',
  TRAINIGS = 'trainings',
  FCA_LEARNING = 'fcaLearning',
  OBSERVATIONS = 'observations',
  INFORMATION = 'information',
}

export const tabMap: {
  [key: string]: { id: string; key: string; title: string; permissionsExpression?: string };
} = {
  [DetailTabType.PROJECTS]: {
    id: 'projects',
    key: 'projects',
    title: 'Projects',
    permissionsExpression: UserModel.ProjectsPermission.VIEWACCESS,
  },
  [DetailTabType.ACTIVITY]: {
    id: 'activity',
    key: 'activity',
    title: 'Activity',
    permissionsExpression: UserModel.WorkersPermission.VIEWACCESS,
  },
  [DetailTabType.CERTIFICATIONS]: {
    id: 'certifications',
    key: 'certifications',
    title: 'Certifications',
    permissionsExpression: UserModel.WorkerCertificationsPermission.VIEWACCESS,
  },
  [DetailTabType.TRAINIGS]: {
    id: 'trainings',
    key: 'trainings',
    title: 'Trainings',
    permissionsExpression: UserModel.WorkerTrainingsPermission.VIEWACCESS,
  },
  [DetailTabType.FCA_LEARNING]: {
    id: 'fcaLearning',
    key: 'fcaLearning',
    title: 'FCA Learning',
    permissionsExpression: UserModel.WorkersPermission.VIEWACCESS,
  },
  [DetailTabType.OBSERVATIONS]: {
    id: 'observations',
    key: 'observations',
    title: 'Observations',
    permissionsExpression: `${UserModel.WorkerObservationsPermission.VIEWACCESS}`,
  },
  [DetailTabType.INFORMATION]: {
    id: 'information',
    key: 'information',
    title: 'Information',
    permissionsExpression: UserModel.WorkersPermission.VIEWACCESS,
  },
};

export const tabList: { id: string; key: string; title: string; permissionsExpression?: string }[] = [
  {
    id: 'projects',
    key: 'projects',
    title: 'Projects',
    permissionsExpression: UserModel.ProjectsPermission.VIEWACCESS,
  },
  {
    id: 'activity',
    key: 'activity',
    title: 'Activity',
    permissionsExpression: UserModel.WorkersPermission.VIEWACCESS,
  },
  {
    id: 'certifications',
    key: 'certifications',
    title: 'Certifications',
    permissionsExpression: UserModel.WorkerCertificationsPermission.VIEWACCESS,
  },
  {
    id: 'trainings',
    key: 'trainings',
    title: 'Trainings',
    permissionsExpression: UserModel.WorkerTrainingsPermission.VIEWACCESS,
  },
  {
    id: 'fcaLearning',
    key: 'fcaLearning',
    title: 'FCA Learning',
    permissionsExpression: UserModel.WorkersPermission.VIEWACCESS,
  },
  {
    id: 'observations',
    key: 'observations',
    title: 'Observations',
    permissionsExpression: `${UserModel.WorkerObservationsPermission.VIEWACCESS}`,
  },
  {
    id: 'information',
    key: 'information',
    title: 'Information',
    permissionsExpression: UserModel.WorkersPermission.VIEWACCESS,
  },
];

export const workerTabMap = {
  [WorkerStatusFilter.MIGRATED]: {
    id: WorkerStatusFilter.MIGRATED,
    key: 'pending-invitation',
    title: 'Unverified',
    order: 0,
  },
  [WorkerStatusFilter.PENDING_REGISTRATION]: {
    id: WorkerStatusFilter.PENDING_REGISTRATION,
    key: 'pending-registration',
    title: 'Invitation Sent',
    order: 1,
  },
  [WorkerStatusFilter.PENDING_PROJECT]: {
    id: WorkerStatusFilter.PENDING_PROJECT,
    key: 'pending-project',
    title: 'Pending Project',
    order: 2,
  },
  [WorkerStatusFilter.ACTIVE]: {
    id: WorkerStatusFilter.ACTIVE,
    key: 'active',
    title: 'Active',
    order: 3,
  },
};

export const workerInviteMap = {
  [InvitationStatus.ACCEPTED]: 'Active',
  [InvitationStatus.PENDING]: 'Pending Registration',
  [InvitationStatus.EXPIRED]: 'Expired',
  [InvitationStatus.MIGRATED]: 'Pending Invitation',
  [InvitationStatus.NOT_INVITED]: 'Unverified',
};

export const isSkilledOptionList = [
  { value: 1, label: 'Skilled' },
  { value: 0, label: 'Non Skilled' },
];

export const isSupervisorOptionList = [
  { value: 1, label: 'Supervisor' },
  { value: 0, label: 'Non Supervisor' },
];

export const paymentTypeMap = {
  [PaymentType.HOURLY]: 'Hourly',
  [PaymentType.SALARY]: 'Salary',
};

export const paymentTypeList = [
  { value: PaymentType.HOURLY, label: paymentTypeMap[PaymentType.HOURLY] },
  { value: PaymentType.SALARY, label: paymentTypeMap[PaymentType.SALARY] },
];

export const badgeTypeMap = {
  [BadgeType.REGULAR]: 'Regular',
  [BadgeType.UNIVERSAL]: 'Universal',
};

export const badgeTypeList = [
  { value: BadgeType.REGULAR, label: badgeTypeMap[BadgeType.REGULAR] },
  { value: BadgeType.UNIVERSAL, label: badgeTypeMap[BadgeType.UNIVERSAL] },
];

export enum IsAllowed {
  ALLOWED = 0,
  NOT_ALLOWED = 1,
}

export const confirmModalPropsMap = {
  [IsAllowed.ALLOWED]: ({ onClose, content, secondaryActionText, confirmLabel, onConfirm, secondaryAction }) => ({
    title: 'Worker may exist',
    content,
    closeLabel: 'Cancel',
    isLoading: false,
    secondaryActionText: secondaryActionText,
    secondaryAction: secondaryAction,
    confirmLabel: confirmLabel,
    onConfirm: onConfirm,
    onClose: onClose,
  }),
  [IsAllowed.NOT_ALLOWED]: ({ onClose, content, onConfirm }) => ({
    title: 'Worker may exist',
    content,
    closeLabel: 'Cancel',
    isLoading: false,
    confirmLabel: 'Contact FCA Admin',
    onConfirm: onConfirm,
    onClose: onClose,
  }),
};

export const getFallbackWorker = (): IWorker => ({
  id: null,
  company: null,
  firstName: null,
  middleName: null,
  lastName: null,
  mobilePhoneNumber: null,
  phoneNumber: null,
  email: null,
  dateOfBirth: null,
  gender: null,
  ethnicity: null,
  ethnicityId: null,
  socialSecurityNumber: null,
  isVeteran: null,
  primaryLanguage: null,
  primaryLanguageId: null,
  otherPrimaryLanguage: null,
  allergies: null,
  identificationType: null,
  identificationTypeId: null,
  identificationNumber: null,
  identificationGeographicLocation: null,
  identificationGeographicLocationId: null,
  otherTrade: null,
  address: null,
  emergencyContactName: null,
  emergencyContactPhone: null,
  emergencyContactRelationship: null,
  isSkilled: null,
  isSupervisor: null,
  laborUnionNumber: null,
  trades: [],
  tradesIds: [],
  invitationStatus: null,
  isAffiliatedToLaborUnion: null,
  inviteMethod: InviteMethod.MOBILE_PHONE,
  line1: null,
  line2: null,
  city: null,
  stateCode: null,
  zipCode: null,
  country: null,
  countryId: null,
  county: null,
  attention1: null,
  attention2: null,
  stateName: null,
  borough: null,
  latitude: null,
  longitude: null,
});

export enum ProjectVisibilityType {
  NotApplicable = 1,
  Show = 2,
  Hide = 3,
}

export interface IWorkerControlSettings {
  canViewAllWorkers: boolean;
  canAssignAllWorkers: boolean;
  canCreateOrManageAllWorkers: boolean;
  projectWorkerControlSettings?: {
    projectId: string;
    projectName: string;
    canViewAllWorkers: boolean;
    canAssignAllWorkers: boolean;
    canCreateOrManageAllWorkers: boolean;
  }[];
}

export enum GCCompanyAccessType {
  View = 0,
  Assign = 1,
  Manage = 2,
  AssignAndManage = 3,
}

export enum TransferWorkerPaymentInstruction {
  NoCharge = 0,
  ChargeImmediately = 1,
  ChargeOnActivation = 2,
}

export enum TransferWorkerBillingEventStatus {
  None = 0,
  Pending,
  Paid,
}

export enum TransferWorkerBillingInstructionType {
  None = 0,
  Refund,
  Delete,
}

export const transferWorkerPaymentInstructionMap = {
  [TransferWorkerPaymentInstruction.NoCharge]: 'No Charge',
  [TransferWorkerPaymentInstruction.ChargeImmediately]: 'Charge Immediately',
  [TransferWorkerPaymentInstruction.ChargeOnActivation]: 'Charge on Activation',
};

export const transferWorkerBillingEventStatusMap = {
  [TransferWorkerBillingEventStatus.None]: 'None',
  [TransferWorkerBillingEventStatus.Pending]: 'Pending',
  [TransferWorkerBillingEventStatus.Paid]: 'Paid',
};

export const transferWorkerBillingInstructionTypeMap = {
  [TransferWorkerBillingInstructionType.None]: 'None',
  [TransferWorkerBillingInstructionType.Refund]: 'Refund',
  [TransferWorkerBillingInstructionType.Delete]: 'Delete',
};

export const getFuturePaymentInstructionTypeOptions = (currentBadgeStatus, futureBadgeStatus, hasBadgeBeenActivatedOnce) => {
  let options = [];
  switch (currentBadgeStatus) {
    case BadgeStatus.ACTIVE:
      if (futureBadgeStatus === BadgeStatus.ACTIVE) {
        options = [
          { label: transferWorkerPaymentInstructionMap[TransferWorkerPaymentInstruction.NoCharge], value: TransferWorkerPaymentInstruction.NoCharge },
          {
            label: transferWorkerPaymentInstructionMap[TransferWorkerPaymentInstruction.ChargeImmediately],
            value: TransferWorkerPaymentInstruction.ChargeImmediately,
          },
        ];
      } else {
        options = [
          { label: transferWorkerPaymentInstructionMap[TransferWorkerPaymentInstruction.NoCharge], value: TransferWorkerPaymentInstruction.NoCharge },
          {
            label: transferWorkerPaymentInstructionMap[TransferWorkerPaymentInstruction.ChargeOnActivation],
            value: TransferWorkerPaymentInstruction.ChargeOnActivation,
          },
        ];
      }
      return options;
    case BadgeStatus.DEACTIVATE:
      if (hasBadgeBeenActivatedOnce) {
        options = [
          { label: transferWorkerPaymentInstructionMap[TransferWorkerPaymentInstruction.NoCharge], value: TransferWorkerPaymentInstruction.NoCharge },
          {
            label: transferWorkerPaymentInstructionMap[TransferWorkerPaymentInstruction.ChargeOnActivation],
            value: TransferWorkerPaymentInstruction.ChargeOnActivation,
          },
        ];
      } else {
        options = [
          {
            label: transferWorkerPaymentInstructionMap[TransferWorkerPaymentInstruction.ChargeOnActivation],
            value: TransferWorkerPaymentInstruction.ChargeOnActivation,
          },
        ];
      }
      return options;
    case BadgeStatus.EXPIRED:
    case BadgeStatus.REVOKED:
      options = [
        { label: transferWorkerPaymentInstructionMap[TransferWorkerPaymentInstruction.NoCharge], value: TransferWorkerPaymentInstruction.NoCharge },
        {
          label: transferWorkerPaymentInstructionMap[TransferWorkerPaymentInstruction.ChargeOnActivation],
          value: TransferWorkerPaymentInstruction.ChargeOnActivation,
        },
      ];
      return options;
    default:
      return options;
  }
};

export const getBillingInstructionTypeOptions = billingEventStatus => {
  let options = [];
  switch (billingEventStatus) {
    case TransferWorkerBillingEventStatus.None:
      options = [
        { label: transferWorkerBillingInstructionTypeMap[TransferWorkerBillingInstructionType.None], value: TransferWorkerBillingInstructionType.None },
      ];
      return options;
    case TransferWorkerBillingEventStatus.Pending:
      options = [
        { label: transferWorkerBillingInstructionTypeMap[TransferWorkerBillingInstructionType.None], value: TransferWorkerBillingInstructionType.None },
        {
          label: transferWorkerBillingInstructionTypeMap[TransferWorkerBillingInstructionType.Delete],
          value: TransferWorkerBillingInstructionType.Delete,
        },
      ];
      return options;
    case TransferWorkerBillingEventStatus.Paid:
      options = [
        { label: transferWorkerBillingInstructionTypeMap[TransferWorkerBillingInstructionType.None], value: TransferWorkerBillingInstructionType.None },
        {
          label: transferWorkerBillingInstructionTypeMap[TransferWorkerBillingInstructionType.Refund],
          value: TransferWorkerBillingInstructionType.Refund,
        },
      ];
      return options;
    default:
      return options;
  }
};
